// Community Challenges with Leaderboard
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Trophy, 
  Users, 
  Target,
  Crown,
  ChevronRight,
  Star,
  Zap,
  Calendar,
  TrendingUp,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weekly community challenges (static for now)
const WEEKLY_CHALLENGES = [
  {
    id: 'steps_week',
    name: 'Marathon de pas',
    description: '50 000 pas cette semaine',
    icon: '👟',
    type: 'steps',
    target: 50000,
    duration: '7 jours',
    reward: '🏆 Badge Marcheur',
    participants: 1247,
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'protein_week',
    name: 'Boost Protéines',
    description: 'Atteindre son objectif protéines 5 jours de suite',
    icon: '💪',
    type: 'protein',
    target: 5,
    duration: '7 jours',
    reward: '🥇 Badge Musclé',
    participants: 856,
    color: 'from-purple-500 to-violet-600'
  },
  {
    id: 'water_week',
    name: 'Hydratation Max',
    description: '8 verres d\'eau par jour pendant 1 semaine',
    icon: '💧',
    type: 'water',
    target: 56,
    duration: '7 jours',
    reward: '💎 Badge Hydraté',
    participants: 2103,
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'calorie_week',
    name: 'Déficit Maîtrisé',
    description: 'Rester en déficit calorique 5 jours',
    icon: '🔥',
    type: 'calories',
    target: 5,
    duration: '7 jours',
    reward: '⭐ Badge Discipliné',
    participants: 634,
    color: 'from-orange-500 to-red-600'
  }
];

export default function CommunityChallenges({ userId, language = 'fr' }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [weeklyChallenges] = useState(WEEKLY_CHALLENGES);
  const [joinedWeekly, setJoinedWeekly] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [pointsToday, setPointsToday] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [challengeToShare, setChallengeToShare] = useState(null);
  
  useEffect(() => {
    fetchChallenges();
    fetchLeaderboard();
    // Load joined weekly challenges from localStorage
    const saved = localStorage.getItem('joined_weekly_challenges');
    if (saved) {
      setJoinedWeekly(JSON.parse(saved));
    }
  }, []);
  
  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/challenges`, { withCredentials: true });
      setDailyChallenges(response.data.daily || []);
      setTotalPoints(response.data.total_points || 0);
      setPointsToday(response.data.points_earned_today || 0);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      toast.error('Erreur lors du chargement des défis');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/challenges/leaderboard`, { withCredentials: true });
      setLeaderboard(response.data.leaderboard || []);
      setUserRank(response.data.user_rank || 0);
    } catch (error) {
      // Use mock data if endpoint doesn't exist yet
      setLeaderboard([
        { rank: 1, name: 'Sophie M.', score: 98500, avatar: '👩', badge: '🥇' },
        { rank: 2, name: 'Thomas L.', score: 87200, avatar: '👨', badge: '🥈' },
        { rank: 3, name: 'Marie D.', score: 82100, avatar: '👩‍🦰', badge: '🥉' },
        { rank: 4, name: 'Lucas P.', score: 76400, avatar: '👨‍🦱', badge: '' },
        { rank: 5, name: 'Emma R.', score: 71800, avatar: '👧', badge: '' },
        { rank: 6, name: 'Hugo B.', score: 68200, avatar: '👦', badge: '' },
        { rank: 7, name: 'Léa C.', score: 65100, avatar: '👩‍🦳', badge: '' },
        { rank: 8, name: 'Nathan V.', score: 61500, avatar: '🧑', badge: '' },
        { rank: 9, name: 'Camille S.', score: 58900, avatar: '👩‍🦱', badge: '' },
        { rank: 10, name: 'Maxime G.', score: 55200, avatar: '👨‍🦲', badge: '' },
      ]);
      setUserRank(42);
    }
  };
  
  const joinWeeklyChallenge = (challengeId) => {
    if (joinedWeekly.includes(challengeId)) {
      toast.info('Vous participez déjà à ce défi !');
      return;
    }
    
    const newJoined = [...joinedWeekly, challengeId];
    setJoinedWeekly(newJoined);
    localStorage.setItem('joined_weekly_challenges', JSON.stringify(newJoined));
    toast.success('Défi rejoint ! Bonne chance 💪');
  };
  
  const shareChallenge = async (challenge) => {
    try {
      await axios.post(`${API}/social/share-challenge`, {
        challenge_id: challenge.id,
        challenge_title: challenge.title || challenge.name,
        message: `J'ai complété le défi "${challenge.title || challenge.name}" ! 🎉`
      }, { withCredentials: true });
      toast.success('Défi partagé sur la communauté !');
      setShowShareDialog(false);
    } catch (error) {
      toast.error('Erreur lors du partage');
    }
  };
  
  const getChallengeProgress = (challenge) => {
    if (challenge.progress !== undefined && challenge.target) {
      return Math.min(100, Math.round((challenge.progress / challenge.target) * 100));
    }
    return Math.floor(Math.random() * 60) + 20;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Points Summary */}
      <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total points</p>
                <p className="text-2xl font-bold text-primary">{totalPoints.toLocaleString()}</p>
              </div>
            </div>
            {pointsToday > 0 && (
              <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                +{pointsToday} aujourd'hui
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="daily" className="gap-2">
            <Zap className="w-4 h-4" />
            Quotidiens
          </TabsTrigger>
          <TabsTrigger value="weekly" className="gap-2">
            <Target className="w-4 h-4" />
            Hebdo
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2">
            <Trophy className="w-4 h-4" />
            Classement
          </TabsTrigger>
        </TabsList>
        
        {/* Daily Challenges Tab */}
        <TabsContent value="daily" className="space-y-3 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              Défis du jour
            </h3>
            <Button variant="ghost" size="sm" onClick={fetchChallenges}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {dailyChallenges.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                Aucun défi disponible pour le moment
              </CardContent>
            </Card>
          ) : (
            dailyChallenges.map(challenge => {
              const progress = getChallengeProgress(challenge);
              const isCompleted = challenge.completed;
              const isClaimed = challenge.already_claimed;
              
              return (
                <Card 
                  key={challenge.id} 
                  className={`overflow-hidden transition-all ${isCompleted ? 'border-green-500/50 bg-green-500/5' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                        isCompleted ? 'bg-green-500/20' : 'bg-muted'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="w-6 h-6 text-green-500" /> : challenge.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">{challenge.title}</h4>
                          <div className="flex items-center gap-2">
                            {isCompleted && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => { setChallengeToShare(challenge); setShowShareDialog(true); }}
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Badge variant={isCompleted ? "default" : "secondary"} className="text-xs">
                              {isCompleted && isClaimed ? '✓ Réclamé' : isCompleted ? `+${challenge.reward} pts` : `${challenge.reward} pts`}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{challenge.description}</p>
                        
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span>{challenge.progress || 0} / {challenge.target}</span>
                            <span className="text-muted-foreground">{progress}%</span>
                          </div>
                          <Progress value={progress} className={`h-2 ${isCompleted ? '[&>div]:bg-green-500' : ''}`} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
        
        {/* Weekly Challenges Tab */}
        <TabsContent value="weekly" className="space-y-3 mt-4">
          {/* Joined weekly challenges */}
          {joinedWeekly.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Mes défis en cours
              </h3>
              {weeklyChallenges.filter(c => joinedWeekly.includes(c.id)).map(challenge => {
                const progress = getChallengeProgress(challenge);
                return (
                  <Card key={challenge.id} className="mb-2 overflow-hidden">
                    <div className={`h-1 bg-gradient-to-r ${challenge.color}`} style={{ width: `${progress}%` }} />
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{challenge.icon}</span>
                          <div>
                            <p className="font-medium text-sm">{challenge.name}</p>
                            <p className="text-xs text-muted-foreground">{progress}% complété</p>
                          </div>
                        </div>
                        <Badge variant="outline">{challenge.duration}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          {/* Available weekly challenges */}
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Target className="w-4 h-4" />
            Défis disponibles
          </h3>
          
          {weeklyChallenges.filter(c => !joinedWeekly.includes(c.id)).map(challenge => (
            <Card 
              key={challenge.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => { setSelectedChallenge(challenge); setShowDetailDialog(true); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${challenge.color} flex items-center justify-center text-2xl`}>
                    {challenge.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{challenge.name}</h4>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{challenge.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="secondary" className="text-xs">
                        <Calendar className="w-3 h-3 mr-1" />
                        {challenge.duration}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {challenge.participants.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        
        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="mt-4">
          {/* User rank card */}
          <Card className="mb-4 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-xl">
                    👤
                  </div>
                  <div>
                    <p className="font-medium">Votre classement</p>
                    <p className="text-2xl font-bold text-primary">#{userRank || '—'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Score total</p>
                  <p className="text-lg font-bold">{totalPoints.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Top 10 leaderboard */}
          <div className="space-y-2">
            {leaderboard.map((user, index) => (
              <div 
                key={user.rank}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  index < 3 ? 'bg-gradient-to-r from-yellow-500/10 to-amber-500/5' : 'bg-muted/30'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  index === 0 ? 'bg-yellow-500 text-black' :
                  index === 1 ? 'bg-gray-300 text-black' :
                  index === 2 ? 'bg-amber-600 text-white' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {user.rank}
                </div>
                <span className="text-xl">{user.avatar}</span>
                <div className="flex-1">
                  <p className="font-medium text-sm">{user.name} {user.badge}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">{user.score.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">points</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Challenge detail dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-sm">
          {selectedChallenge && (
            <>
              <DialogHeader>
                <div className={`w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br ${selectedChallenge.color} flex items-center justify-center text-4xl mb-2`}>
                  {selectedChallenge.icon}
                </div>
                <DialogTitle className="text-center">{selectedChallenge.name}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-center text-muted-foreground">{selectedChallenge.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 rounded-lg bg-muted">
                    <Calendar className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium">{selectedChallenge.duration}</p>
                    <p className="text-xs text-muted-foreground">Durée</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted">
                    <Users className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium">{selectedChallenge.participants?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">Participants</p>
                  </div>
                </div>
                
                <div className="text-center p-4 rounded-xl bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30">
                  <p className="text-sm text-muted-foreground mb-1">Récompense</p>
                  <p className="text-lg font-bold">{selectedChallenge.reward}</p>
                </div>
                
                <Button 
                  className={`w-full bg-gradient-to-r ${selectedChallenge.color} hover:opacity-90`}
                  onClick={() => { joinWeeklyChallenge(selectedChallenge.id); setShowDetailDialog(false); }}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Rejoindre le défi
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Share challenge dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm">
          {challengeToShare && (
            <>
              <DialogHeader>
                <DialogTitle className="text-center">Partager votre réussite</DialogTitle>
              </DialogHeader>
              
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                
                <div>
                  <p className="font-medium">{challengeToShare.title || challengeToShare.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Félicitations ! Vous avez complété ce défi.
                  </p>
                </div>
                
                <Button
                  className="w-full bg-gradient-to-r from-primary to-secondary"
                  onClick={() => shareChallenge(challengeToShare)}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Partager sur la communauté
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
